import { QueryKey } from '@tanstack/react-query'
import { AllOperationIds } from '../schema'
import {
  QueryKeyIdentifiers,
  Resources,
  isIdentifierForCollection,
  isIdentifierForSingleResource,
} from './types'

type BuildQueryKeyArgs<TOperationId extends AllOperationIds> = {
  /**
   * Additional parameters and arguments used in the API request (resourceId,
   * query params, etc.). These values are passed from the component making
   * the request, while the `resource` and `operationId` are static values
   * defined in API documentation.
   */
  identifiers: QueryKeyIdentifiers<TOperationId>
  /**
   * The name of operationID documented for the API action. A null value will
   * scope the queryKey to all requests related to a resource for the workspace
   */
  operationId: AllOperationIds | null
  /** The name of resource per the API route */
  resource: Resources
}

/**
 * Builds a QueryKey using the provided arguments in a set order. Arguments with
 * null values will not be returned as part of the queryKey. The `resource` name
 * and `workspaceUuid` of identifiers are always required. Key is ordered as:
 *
 * `[resourceName, workspaceUuid, operationId?, resourceId?, queryParams?]`
 */
export const buildQueryKey = <TOperationId extends AllOperationIds>({
  identifiers,
  operationId,
  resource,
}: BuildQueryKeyArgs<TOperationId>): QueryKey => {
  let key: QueryKey = [resource, identifiers.workspaceUuid]

  if (operationId) {
    key = [...key, operationId]
  }
  if (isIdentifierForSingleResource(identifiers)) {
    key = [...key, identifiers.resourceId]
  }
  if (isIdentifierForCollection(identifiers)) {
    key = [...key, identifiers.queryParams]
  }

  return key
}
